import Link from '@/components/Link';
import { isBrowser } from '@/utils/environment';
import type { CheckoutFormInitiatedOnType } from '@gik/checkout/types';
import { createCartItemFromProduct } from '@gik/checkout/utils/productUtils';
import { withMeta } from '@gik/core/hocs/withMeta';
import type { CartItem } from '@gik/core/models/gik/Order';
import type { Product } from '@gik/core/models/gik/Product';
import routes from '@gik/core/routes';
import { useDevStore } from '@gik/core/store/DevStore';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { ProductDescription } from '@gik/shop/components/products/ProductDescription/ProductDescription';
import { ProductDetails } from '@gik/shop/components/products/ProductDetails/ProductDetails';
import { ProductPreview } from '@gik/shop/components/products/ProductPreview/ProductPreview';
import { getOpenLoopType, useOpenLoop } from '@gik/shop/hooks/useOpenLoop';
import { getPGOLExpirationMonth } from '@gik/shop/hooks/usePGOLExpirationMonth';
import { Button } from '@gik/ui/Button';
import type { FormRef } from '@gik/ui/Form';
import { SvgIcon } from '@gik/ui/SvgIcon';
import ChevronLeftIcon from '@heroicons/react/outline/ChevronLeftIcon';
import React from 'react';
import { PerfectgiftFaceplatePreview } from '../../PerfectgiftFaceplatePreview/PerfectgiftFaceplatePreview';
import { ProductActions } from '../ProductActions/ProductActions';

export type OLProductType = 'physical' | 'digital';

export type ProductSectionProps = {
  inkindRouteId: string;
  productSlug: string;
  type: CheckoutFormInitiatedOnType;
  // initial data
  products?: Product[];
  skeletons?: boolean;
  useLiveEditor?: boolean;
} & UIComponent;

function ProductSectionComp({
  inkindRouteId,
  type,
  products,
  skeletons,
  useLiveEditor = false,
}: ProductSectionProps): React.ReactElement {
  const bem = useBemCN('product-section');

  const [selectedCartItem, setSelectedCartItem] = React.useState<CartItem>();
  const [screenWidth, setScreenWidth] = React.useState<number>(0);
  const devStoreSkeleton = useDevStore(state => state.forceSkeletons);
  const forceSkeleton = React.useMemo(() => devStoreSkeleton || skeletons, [devStoreSkeleton, skeletons]);
  const product = React.useMemo(() => (forceSkeleton ? undefined : products?.[0]), [forceSkeleton, products]);

  const [zoom, setZoom] = React.useState<number>(1);

  React.useEffect(() => {
    if (isBrowser()) {
      setScreenWidth(window.screen.width - 64);
    }
  }, []);

  const olCardWidth = 400;

  const { croppedImage, showMode, hasPhysicalCard, designUploader, handleCustomImageChange, handleFaceplateChange } =
    useOpenLoop(product, selectedCartItem?.productType);

  React.useEffect(() => {
    if ((product?.variations?.length || 0) <= 1 || product?.isToggleable) {
      setSelectedCartItem(createCartItemFromProduct(product));
    }
  }, [product]);

  const pageTitle = React.useMemo(() => product?.name, [product?.name]);
  const pageDescription = React.useMemo(() => product?.description, [product?.description]);
  const pageKeywords = React.useMemo(() => product?.situationKeyword, [product?.situationKeyword]);
  const pageImage = React.useMemo(() => product?.images[0], [product?.images]);
  const formRef = React.useRef<FormRef>();

  function handleChange(newItem: CartItem) {
    const payload = {
      ...selectedCartItem,
      ...(newItem ?? {}),
      ...(newItem?.productType ? { expiryMonth: getPGOLExpirationMonth(product, newItem.productType) } : {}),
    };

    if (newItem?.productType) {
      const variationId = product.variations?.find(v =>
        v.metaData?.find(m => m.key === 'pg_product_type' && m.value === newItem.productType)
      )?.id;

      if (variationId && selectedCartItem?.variationId !== variationId) payload.variationId = variationId;
    }

    setSelectedCartItem(payload);

    if (newItem?.faceplate) handleFaceplateChange(newItem.faceplate);
    if (newItem?.uploadPhoto) void handleCustomImageChange(newItem.uploadPhoto);
  }

  const handlePickAlternativeProductVariation = React.useCallback(
    async function handlePickAlternativeProductVariation() {
      await formRef.current?.setFieldValue('productType-0', 'digital');
    },
    []
  );

  const handleResize = React.useCallback(() => {
    const screenWidth = window.screen.width - 64;
    let zoom = screenWidth / olCardWidth;

    if (window.innerWidth > 768) zoom = 1;
    setZoom(zoom);
  }, []);

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  if (!product) {
    return null;
  }

  return withMeta(
    {
      title: pageTitle,
      imageUrl: pageImage,
      description: pageDescription,
      keywords: pageKeywords,
      type: 'product',
    },
    <div {...bem()} role="layout">
      {inkindRouteId && (
        <div {...bem('header-nav')}>
          <Link href={`${routes.inkinds}/${inkindRouteId}`}>
            <Button variant="primary-inverted" pill size="sm" prepend={<SvgIcon Icon={ChevronLeftIcon} size="sm" />}>
              Back
            </Button>
          </Link>
        </div>
      )}

      <header {...bem('header')} role="header">
        <div {...bem('preview-wrapper')}>
          {(designUploader || hasPhysicalCard) && useLiveEditor ? (
            <div className="gik-product-preview">
              <PerfectgiftFaceplatePreview
                expiryMonth={selectedCartItem?.expiryMonth}
                faceplate={selectedCartItem?.faceplate}
                customImage={croppedImage?.objectUrl}
                customMessage={selectedCartItem?.customMessage}
                showMode={showMode}
                overlay={getOpenLoopType(product)}
                style={{ zoom, margin: '0 auto' }}
              />
            </div>
          ) : (
            <ProductPreview product={product} hasPhysicalCard={hasPhysicalCard} />
          )}
        </div>
        <div {...bem('header-content')}>
          <ProductDetails
            ref={formRef}
            product={product}
            toggleValue={selectedCartItem?.productType}
            onChange={handleChange}
          />
          {selectedCartItem && (
            <ProductActions
              product={product}
              initiatedOn={type}
              inkindRouteId={inkindRouteId}
              selectedCartItem={selectedCartItem}
              formRef={formRef.current}
              onPickAlternativeProductVariation={handlePickAlternativeProductVariation}
            />
          )}
        </div>
      </header>
      <main {...bem('main')} role="content">
        <ProductDescription product={product} toggleValue={selectedCartItem?.productType} cartItem={selectedCartItem} />
      </main>
    </div>
  );
}

export const ProductSection = withComponentErrorBoundary(ProductSectionComp);
